import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { message } from '@bb/elements-web';
import { getOrgContractsByOrgId } from 'api/SupraController';
import {
  getSupraPin,
  getSupraSystemSubscription,
} from 'api/SupraSystemSubscriptionController';
import { AuthContext } from 'contexts';
import type { IGetSupraSubscriptionAnyStatusResponse } from '@/app-package';
import useSingleAccessEnabled from 'hooks/useSingleAccessEnabled';

interface SupraAccessDetailsPageContextShape {
  userId: string;
  orgId: string;
  subscription: IGetSupraSubscriptionAnyStatusResponse | null;
  /** 'org' if Board billing model, 'keyholder' if Agent/User billing model */
  planType: 'org' | 'keyholder';
  /** Numeric cost primitive value (e.g. 40) */
  planCost: number;
  /** Clean text interval designation (e.g. 'month', 'year', 'quarter') */
  billingFrequency: string;
  pin: string | undefined;
  isSingleAccessEnabledForBoard: boolean;
  loading: boolean;
  /** Refetch with loading state — for explicit user-initiated refreshes */
  refetch: () => Promise<void>;
  /** Refetch without toggling loading — for syncing after background mutations */
  silentRefetch: () => Promise<void>;
}

interface ISupraContract {
  contractRateLabel: string;
  rate: string;
  productBillingModel?: string;
  productSubscriptionCycle?: string;
  profileBillingCycle?: string;
}

const SupraAccessDetailsPageContext =
  createContext<SupraAccessDetailsPageContextShape | null>(null);

export const useSupraAccessDetailsPageContext =
  (): SupraAccessDetailsPageContextShape => {
    const ctx = useContext(SupraAccessDetailsPageContext);
    if (!ctx) {
      throw new Error(
        'useSupraAccessDetailsPageContext must be used within a SupraAccessDetailsPageProvider',
      );
    }
    return ctx;
  };

export const SupraAccessDetailsPageProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { auth } = useContext(AuthContext);
  const userId = auth.id;
  const orgId = auth.org.id;

  const [subscription, setSubscription] =
    useState<IGetSupraSubscriptionAnyStatusResponse | null>(null);
  const [pin, setPin] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);

  const [planType, setPlanType] = useState<'org' | 'keyholder'>('org');
  const [planCost, setPlanCost] = useState<number>(0);
  const [billingFrequency, setBillingFrequency] = useState<string>('');

  const isSingleAccessEnabledForBoard = useSingleAccessEnabled({ userId });

  const fetchData = useCallback(
    async (silent = false) => {
      if (!userId) return;
      if (!silent) setLoading(true);
      try {
        const sub = await getSupraSystemSubscription({ userId });
        setSubscription(sub);

        if (sub?.status === 'subscribed') {
          const fetchedPin = await getSupraPin(userId);
          setPin(fetchedPin != null ? String(fetchedPin) : undefined);

          if (sub.type && sub.billingProductName && sub.orgId) {
            const response = await getOrgContractsByOrgId({
              orgId: sub.orgId.toString(),
            });

            const contracts = response as ISupraContract[];

            const subscriptionContract = contracts.find(
              singleContract =>
                singleContract.contractRateLabel === sub.billingProductName,
            );

            if (
              subscriptionContract &&
              subscriptionContract.productBillingModel === 'Board'
            ) {
              setPlanType('org');
              setPlanCost(0);
              setBillingFrequency('');
            } else if (subscriptionContract) {
              setPlanType('keyholder');

              const payRate = subscriptionContract.rate || '0';
              setPlanCost(parseFloat(payRate.replace('$', '')));

              const frequencyCycle =
                subscriptionContract.productSubscriptionCycle ||
                subscriptionContract.profileBillingCycle ||
                '';
              switch (frequencyCycle) {
                case 'YEARLY':
                  setBillingFrequency('year');
                  break;
                case 'MONTHLY':
                  setBillingFrequency('month');
                  break;
                case 'QUARTERLY':
                  setBillingFrequency('quarter');
                  break;
                default:
                  setBillingFrequency('');
              }
            } else {
              setPlanType('org');
            }
          }
        } else {
          setPin(undefined);
          setPlanType('org');
        }
      } catch {
        message.error('Failed to load Supra subscription details.');
        setSubscription(null);
        setPin(undefined);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [userId],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <SupraAccessDetailsPageContext.Provider
      value={{
        userId,
        orgId,
        subscription,
        planType,
        planCost,
        billingFrequency,
        pin,
        isSingleAccessEnabledForBoard,
        loading,
        refetch: () => fetchData(false),
        silentRefetch: () => fetchData(true),
      }}
    >
      {children}
    </SupraAccessDetailsPageContext.Provider>
  );
};

export default SupraAccessDetailsPageContext;
